import { useEffect, useRef, useState } from 'react';

interface UseAnimatedCounterOptions {
  targetValue: number;
  duration?: number;
  decimals?: number;
}

interface UseAnimatedCounterReturn {
  value: number;
  isAnimating: boolean;
}

const easeOutExpo = (t: number): number => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);

// Get starting value with same digit count as target (e.g., 191.51 -> 100.00)
const getStartValue = (target: number): number => {
  if (target < 1) return 0;
  const intPart = Math.floor(target);
  const digitCount = intPart.toString().length;
  return Math.pow(10, digitCount - 1);
};

export const useAnimatedCounter = ({
  targetValue,
  duration = 1500,
  decimals = 2
}: UseAnimatedCounterOptions): UseAnimatedCounterReturn => {
  const startValue = getStartValue(targetValue);
  const [value, setValue] = useState(startValue);
  const [isAnimating, setIsAnimating] = useState(false);
  const frameRef = useRef<number>();
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      setValue(targetValue);
      return;
    }

    // Reset to start value immediately to prevent flicker
    setValue(startValue);
    setIsAnimating(true);
    startTimeRef.current = null;

    const animate = (currentTime: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = currentTime;
      }
      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutExpo(progress);
      
      const currentValue = startValue + (targetValue - startValue) * easedProgress;
      setValue(Number(currentValue.toFixed(decimals)));

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [targetValue, duration, decimals, startValue]);

  useEffect(() => {
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return { value, isAnimating };
};
